// src/pages/finance-portal/tax-planning/TaxPlanning.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    IconButton, Tooltip, Checkbox,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AddIcon from '@mui/icons-material/Add';
import CalculateIcon from '@mui/icons-material/Calculate';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const SCENARIO_TYPES = [
    { value: 'INCOME_DEFERRAL', label: 'Income Deferral' },
    { value: 'EXPENSE_ACCELERATION', label: 'Expense Acceleration' },
    { value: 'ENTITY_CONVERSION', label: 'Entity Conversion' },
    { value: 'RETIREMENT_CONTRIBUTION', label: 'Retirement Contribution' },
    { value: 'CHARITABLE_GIVING', label: 'Charitable Giving' },
    { value: 'CUSTOM', label: 'Custom' },
];

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export default function TaxPlanning() {
    const { enqueueSnackbar } = useSnackbar();
    const [scenarios, setScenarios] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [taxYear, setTaxYear] = useState(new Date().getFullYear().toString());

    // Create dialog
    const [formOpen, setFormOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '', scenario_type: 'CUSTOM', tax_year: '',
        base_income: '', base_deductions: '',
        adjusted_income: '', adjusted_deductions: '',
        assumptions: '',
    });

    // Compare
    const [selectedIds, setSelectedIds] = useState([]);
    const [compareResult, setCompareResult] = useState(null);
    const [compareOpen, setCompareOpen] = useState(false);
    const [comparing, setComparing] = useState(false);

    const fetchScenarios = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (taxYear) params.tax_year = taxYear;
            const res = await axiosInstance.get('/api/finance/tax/planning/scenarios', { params });
            if (res.data.success) {
                setScenarios(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load scenarios');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, taxYear]);

    useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

    const openCreateForm = () => {
        setFormData({
            name: '', scenario_type: 'CUSTOM', tax_year: taxYear,
            base_income: '', base_deductions: '',
            adjusted_income: '', adjusted_deductions: '',
            assumptions: '',
        });
        setFormOpen(true);
    };

    const handleCreate = async () => {
        setSaving(true);
        try {
            const payload = {
                ...formData,
                tax_year: parseInt(formData.tax_year, 10),
                base_income: parseFloat(formData.base_income) || 0,
                base_deductions: parseFloat(formData.base_deductions) || 0,
                adjusted_income: parseFloat(formData.adjusted_income) || 0,
                adjusted_deductions: parseFloat(formData.adjusted_deductions) || 0,
            };
            await axiosInstance.post('/api/finance/tax/planning/scenarios', payload);
            enqueueSnackbar('Scenario created', { variant: 'success' });
            setFormOpen(false);
            fetchScenarios();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to create scenario', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCalculate = async (id) => {
        try {
            await axiosInstance.post(`/api/finance/tax/planning/scenarios/${id}/calculate`);
            enqueueSnackbar('Scenario calculated', { variant: 'success' });
            fetchScenarios();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Calculation failed', { variant: 'error' });
        }
    };

    const toggleSelection = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleCompare = async () => {
        if (selectedIds.length < 2) {
            enqueueSnackbar('Select at least 2 scenarios to compare', { variant: 'warning' });
            return;
        }
        setComparing(true);
        setCompareResult(null);
        try {
            const res = await axiosInstance.post('/api/finance/tax/planning/scenarios/compare', {
                scenario_ids: selectedIds,
            });
            if (res.data.success) {
                setCompareResult(res.data.data);
                setCompareOpen(true);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Comparison failed', { variant: 'error' });
        } finally {
            setComparing(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <LightbulbIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Planning</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Model scenarios and compare tax outcomes</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            startIcon={comparing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CompareArrowsIcon />}
                            onClick={handleCompare}
                            disabled={comparing || selectedIds.length < 2}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Compare ({selectedIds.length})
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openCreateForm}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Add Scenario
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        size="small" label="Tax Year" type="number" value={taxYear}
                        onChange={e => { setTaxYear(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        sx={{ minWidth: 110 }}
                    />
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell padding="checkbox" sx={headCellSx} />
                                    {['Name', 'Type', 'Year', 'Base Tax', 'Adjusted Tax', 'Tax Savings', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            <TableCell padding="checkbox"><Skeleton width={20} height={20} /></TableCell>
                                            {[1, 2, 3, 4, 5, 6, 7].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}
                                        </TableRow>
                                    ))
                                ) : scenarios.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No scenarios found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : scenarios.map(sc => {
                                    const savings = (sc.base_tax || 0) - (sc.adjusted_tax || 0);
                                    const hasSavings = savings > 0;
                                    return (
                                        <TableRow key={sc.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedIds.includes(sc.id)}
                                                    onChange={() => toggleSelection(sc.id)}
                                                    sx={{ '&.Mui-checked': { color: ACCENT } }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{sc.name}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Chip label={getStatusLabel(sc.scenario_type)} size="small"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.72rem', bgcolor: '#dbeafe', color: '#1e40af' }} />
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>{sc.tax_year}</TableCell>
                                            <TableCell sx={bodyCellSx}>{sc.base_tax != null ? formatCurrency(sc.base_tax) : '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>{sc.adjusted_tax != null ? formatCurrency(sc.adjusted_tax) : '\u2014'}</TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                {sc.tax_savings != null || (sc.base_tax != null && sc.adjusted_tax != null) ? (
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.85rem', fontWeight: 700,
                                                        color: hasSavings ? '#166534' : '#991b1b',
                                                    }}>
                                                        {hasSavings ? '+' : ''}{formatCurrency(sc.tax_savings != null ? sc.tax_savings : savings)}
                                                    </Typography>
                                                ) : '\u2014'}
                                            </TableCell>
                                            <TableCell sx={bodyCellSx}>
                                                <Tooltip title="Calculate">
                                                    <IconButton size="small" onClick={() => handleCalculate(sc.id)}>
                                                        <CalculateIcon sx={{ fontSize: 18, color: '#1e40af' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Create Scenario Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Tax Planning Scenario</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Scenario Name" required
                                value={formData.name}
                                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Scenario Type" required
                                value={formData.scenario_type}
                                onChange={e => setFormData(prev => ({ ...prev, scenario_type: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {SCENARIO_TYPES.map(t => <MenuItem key={t.value} value={t.value} sx={{ fontFamily }}>{t.label}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Tax Year" type="number" required
                                value={formData.tax_year}
                                onChange={e => setFormData(prev => ({ ...prev, tax_year: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', color: '#64748b', mt: 1 }}>Base Scenario</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Base Income" type="number"
                                value={formData.base_income}
                                onChange={e => setFormData(prev => ({ ...prev, base_income: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Base Deductions" type="number"
                                value={formData.base_deductions}
                                onChange={e => setFormData(prev => ({ ...prev, base_deductions: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', color: '#64748b', mt: 1 }}>Adjusted Scenario</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Adjusted Income" type="number"
                                value={formData.adjusted_income}
                                onChange={e => setFormData(prev => ({ ...prev, adjusted_income: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Adjusted Deductions" type="number"
                                value={formData.adjusted_deductions}
                                onChange={e => setFormData(prev => ({ ...prev, adjusted_deductions: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Assumptions / Notes" multiline rows={3}
                                value={formData.assumptions}
                                onChange={e => setFormData(prev => ({ ...prev, assumptions: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={saving || !formData.name}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Comparison Dialog */}
            <Dialog open={compareOpen} onClose={() => setCompareOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Scenario Comparison</DialogTitle>
                <DialogContent>
                    {compareResult && compareResult.scenarios ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Metric</TableCell>
                                        {compareResult.scenarios.map((sc, i) => (
                                            <TableCell key={i} sx={headCellSx}>{sc.name}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[
                                        { label: 'Type', key: 'scenario_type', fmt: getStatusLabel },
                                        { label: 'Base Income', key: 'base_income', fmt: formatCurrency },
                                        { label: 'Base Deductions', key: 'base_deductions', fmt: formatCurrency },
                                        { label: 'Adjusted Income', key: 'adjusted_income', fmt: formatCurrency },
                                        { label: 'Adjusted Deductions', key: 'adjusted_deductions', fmt: formatCurrency },
                                        { label: 'Base Tax', key: 'base_tax', fmt: formatCurrency },
                                        { label: 'Adjusted Tax', key: 'adjusted_tax', fmt: formatCurrency },
                                        { label: 'Tax Savings', key: 'tax_savings', fmt: formatCurrency },
                                    ].map((row, ri) => (
                                        <TableRow key={ri}>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{row.label}</TableCell>
                                            {compareResult.scenarios.map((sc, ci) => {
                                                const val = sc[row.key];
                                                const isSavings = row.key === 'tax_savings';
                                                return (
                                                    <TableCell key={ci} sx={{
                                                        ...bodyCellSx,
                                                        fontWeight: isSavings ? 700 : 400,
                                                        color: isSavings && val > 0 ? '#166534' : isSavings && val < 0 ? '#991b1b' : '#1e293b',
                                                    }}>
                                                        {val != null ? row.fmt(val) : '\u2014'}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : compareResult && compareResult.length ? (
                        /* Alternative: array of scenarios directly */
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={headCellSx}>Metric</TableCell>
                                        {compareResult.map((sc, i) => (
                                            <TableCell key={i} sx={headCellSx}>{sc.name}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[
                                        { label: 'Type', key: 'scenario_type', fmt: getStatusLabel },
                                        { label: 'Base Income', key: 'base_income', fmt: formatCurrency },
                                        { label: 'Base Deductions', key: 'base_deductions', fmt: formatCurrency },
                                        { label: 'Adjusted Income', key: 'adjusted_income', fmt: formatCurrency },
                                        { label: 'Adjusted Deductions', key: 'adjusted_deductions', fmt: formatCurrency },
                                        { label: 'Base Tax', key: 'base_tax', fmt: formatCurrency },
                                        { label: 'Adjusted Tax', key: 'adjusted_tax', fmt: formatCurrency },
                                        { label: 'Tax Savings', key: 'tax_savings', fmt: formatCurrency },
                                    ].map((row, ri) => (
                                        <TableRow key={ri}>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{row.label}</TableCell>
                                            {compareResult.map((sc, ci) => {
                                                const val = sc[row.key];
                                                const isSavings = row.key === 'tax_savings';
                                                return (
                                                    <TableCell key={ci} sx={{
                                                        ...bodyCellSx,
                                                        fontWeight: isSavings ? 700 : 400,
                                                        color: isSavings && val > 0 ? '#166534' : isSavings && val < 0 ? '#991b1b' : '#1e293b',
                                                    }}>
                                                        {val != null ? row.fmt(val) : '\u2014'}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography sx={{ fontFamily, color: '#94a3b8', textAlign: 'center', py: 4 }}>No comparison data available</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCompareOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
